import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Users2, LayoutGrid } from 'lucide-react';
import { Avatar, Switch } from '@/components/ui';
import { cn } from '@/lib/utils';
import { ROUTES } from '@/config/constants';
import { stripHandle } from '../helpers';
import { useFriends, useUserCommunities } from '../hooks/useSocial';

interface AboutSectionProps {
  userId: string;
  /** The owner's saved visibility flags (`profiles.show_friends` / `show_communities`). */
  showFriends: boolean;
  showCommunities: boolean;
  /**
   * Self view → render the show/hide toggles and always list the content so the
   * owner can manage it. Public view → respect the flags and hide whatever the
   * owner has opted out of (rendering nothing if both are hidden).
   */
  editable?: boolean;
  onToggleFriends?: (v: boolean) => void;
  onToggleCommunities?: (v: boolean) => void;
  /** Drop the card chrome (border/bg/padding) to sit inside another panel. */
  embedded?: boolean;
  className?: string;
}

const MAX_FRIEND_AVATARS = 6;

/**
 * The profile "About" block: the communities you're a member of and your
 * friends. Each is independently gated by a `profiles`-backed visibility flag —
 * on your own profile you see (and flip) the switches; other people only see
 * what you've chosen to show.
 */
export function AboutSection({
  userId,
  showFriends,
  showCommunities,
  editable,
  onToggleFriends,
  onToggleCommunities,
  embedded,
  className,
}: AboutSectionProps) {
  const { data: communities = [] } = useUserCommunities(userId);
  const { data: friends = [] } = useFriends(userId);

  const communitiesVisible = editable || showCommunities;
  const friendsVisible = editable || showFriends;

  // Public view with everything hidden → render nothing at all.
  if (!editable && !communitiesVisible && !friendsVisible) return null;

  return (
    <section
      className={cn(
        !embedded && 'mb-6 rounded-2xl border border-edge bg-white/[0.03] p-6',
        className,
      )}
    >
      <h2 className="mb-4 text-sm font-extrabold uppercase tracking-[1.5px] text-accent">About</h2>

      {communitiesVisible && (
        <Block
          icon={<LayoutGrid size={14} />}
          title="Communities"
          editable={editable}
          checked={showCommunities}
          onToggle={(v) => onToggleCommunities?.(v)}
        >
          {communities.length === 0 ? (
            <Empty>Not in any communities yet.</Empty>
          ) : (
            <div className="flex flex-wrap gap-2">
              {communities.map((c) => (
                <Link
                  key={c.id}
                  to={ROUTES.community(c.id)}
                  className="inline-flex items-center gap-2 rounded-pill border border-edge bg-white/[0.02] px-3 py-1.5 text-xs font-semibold text-hi transition-colors hover:border-white/15 hover:bg-white/[0.05]"
                >
                  <span
                    className="h-2.5 w-2.5 flex-none rounded-full"
                    style={{ background: c.color }}
                  />
                  {c.name}
                </Link>
              ))}
            </div>
          )}
        </Block>
      )}

      {communitiesVisible && friendsVisible && <div className="my-4 h-px bg-edge" />}

      {friendsVisible && (
        <Block
          icon={<Users2 size={14} />}
          title="Friends"
          count={friends.length}
          editable={editable}
          checked={showFriends}
          onToggle={(v) => onToggleFriends?.(v)}
        >
          {friends.length === 0 ? (
            <Empty>No friends to show yet.</Empty>
          ) : (
            <div className="flex flex-wrap gap-3">
              {friends.slice(0, MAX_FRIEND_AVATARS).map((f) => (
                <Link
                  key={f.id}
                  to={ROUTES.profile(stripHandle(f.handle) || f.id)}
                  className="flex w-14 flex-col items-center gap-1.5 text-center transition-transform hover:-translate-y-0.5"
                  title={f.display_name ?? undefined}
                >
                  <Avatar src={f.avatar_url} name={f.display_name} size={44} />
                  <span className="w-full truncate text-[11px] font-medium text-mid">
                    {f.display_name ?? 'WINR'}
                  </span>
                </Link>
              ))}
              {friends.length > MAX_FRIEND_AVATARS && (
                <div className="flex h-11 w-14 flex-col items-center justify-center text-[11px] font-bold text-lo">
                  +{friends.length - MAX_FRIEND_AVATARS}
                </div>
              )}
            </div>
          )}
        </Block>
      )}
    </section>
  );
}

function Block({
  icon,
  title,
  count,
  editable,
  checked,
  onToggle,
  children,
}: {
  icon: ReactNode;
  title: string;
  count?: number;
  editable?: boolean;
  checked: boolean;
  onToggle: (v: boolean) => void;
  children: ReactNode;
}) {
  return (
    <div>
      <div className="mb-2.5 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-mid">
          <span className="text-lo">{icon}</span>
          {title}
          {typeof count === 'number' && count > 0 && <span className="text-lo">· {count}</span>}
        </div>
        {editable && (
          <label className="flex items-center gap-2 text-[11px] font-semibold text-lo">
            {checked ? 'Shown' : 'Hidden'}
            <Switch checked={checked} onChange={onToggle} aria-label={`Show ${title}`} />
          </label>
        )}
      </div>
      <div className={cn(editable && !checked && 'opacity-40')}>{children}</div>
    </div>
  );
}

function Empty({ children }: { children: ReactNode }) {
  return <p className="text-xs text-lo">{children}</p>;
}
